import { useCallback } from 'react';
import type { Question, SurveyState } from '../../types/surveys/publicSurvey.types';

export const useSurveyValidation = (surveyState: SurveyState) => {
  const validatePage = useCallback((questions: Question[]) => {
    const errors: Record<string, string> = {};
    let isValid = true;

    questions.forEach(question => {
      // Skip validation for hidden child questions
      if (!surveyState.visibleChildQuestions.has(question.id) && 
          question.answerStyle === 'checkbox') {
        return;
      }

      const response = surveyState.surveyData[question.id];

      // Required field validation
      if (question.required) {
        let hasValue = false;

        // Check for star rating questions
        if (question.showFiveStar) {
          hasValue = !!response?.starRating && response.starRating > 0;
        } else {
          switch (question.answerStyle) {
            case 'text':
            case 'textarea':
            case 'number':
            case 'email':
            case 'url':
            case 'date':
              hasValue = !!response?.value && response.value.trim() !== '';
              break;
            case 'radio':
            case 'dropdown':
              if (question.allowMultipleAnswers) {
                hasValue = !!response?.answerIds && response.answerIds.length > 0;
              } else {
                hasValue = !!response?.answerId;
              }
              break;
            case 'checkbox':
            case 'checkboxes':
              hasValue = !!response?.answerIds && response.answerIds.length > 0;
              break;
          }
        }
        
        // Special case: Comment-only questions
        // If question has showComments and no other valid response options,
        // then the comment itself should be considered a valid response
        const hasAnswerOptions = question.answers && question.answers.length > 0;
        
        // A question is comment-only if:
        // 1. It has showComments enabled
        // 2. It has no star rating
        // 3. It either has no answerStyle OR has no answer options (for radio/checkbox)
        const needsAnswerOptions = ['radio', 'checkbox', 'checkboxes', 'dropdown', 'multiselect'].includes(question.answerStyle);
        const isCommentOnlyQuestion = question.showComments && 
                                      !question.showFiveStar && 
                                      (!needsAnswerOptions || !hasAnswerOptions);
        
        if (!hasValue && isCommentOnlyQuestion && response?.comment && response.comment.trim() !== '') {
          hasValue = true;
        }

        if (!hasValue) {
          errors[question.id] = `${question.name} is required`;
          isValid = false;
        }
      }

      // Type-specific validation
      if (response?.value) {
        switch (question.answerStyle) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(response.value)) {
              errors[question.id] = 'Please enter a valid email address';
              isValid = false;
            }
            break;
          case 'url':
            try {
              new URL(response.value);
            } catch {
              errors[question.id] = 'Please enter a valid URL';
              isValid = false;
            }
            break;
          case 'number':
            if (isNaN(Number(response.value))) {
              errors[question.id] = 'Please enter a valid number';
              isValid = false;
            }
            break;
        }

        // Character limit validation
        if (question.options?.useCharacterLimit && question.options?.characterLimit) {
          if (response.value.length > question.options.characterLimit) {
            errors[question.id] = `Maximum ${question.options.characterLimit} characters allowed`;
            isValid = false;
          }
        }
      }
    });

    return { isValid, errors };
  }, [surveyState.surveyData, surveyState.visibleChildQuestions]);

  const validateField = useCallback((question: Question) => {
    const response = surveyState.surveyData[question.id];
    let error: string | null = null;

    // Required field validation
    if (question.required && surveyState.touchedFields.has(question.id)) {
      let hasValue = false;

      // Check for star rating questions
      if (question.showFiveStar) {
        hasValue = !!response?.starRating && response.starRating > 0;
      } else {
        switch (question.answerStyle) {
          case 'text':
          case 'textarea':
          case 'number':
          case 'email':
          case 'url':
          case 'date':
            hasValue = !!response?.value && response.value.trim() !== '';
            break;
          case 'radio':
          case 'dropdown':
            if (question.allowMultipleAnswers) {
              hasValue = !!response?.answerIds && response.answerIds.length > 0;
            } else {
              hasValue = !!response?.answerId;
            }
            break;
          case 'checkbox':
          case 'checkboxes':
            hasValue = !!response?.answerIds && response.answerIds.length > 0;
            break;
        }
      }
      
      // Special case: Comment-only questions
      // If question has showComments and no other valid response options,
      // then the comment itself should be considered a valid response
      const hasAnswerOptions = question.answers && question.answers.length > 0;
      
      // A question is comment-only if:
      // 1. It has showComments enabled
      // 2. It has no star rating
      // 3. It either has no answerStyle OR has no answer options (for radio/checkbox)
      const needsAnswerOptions = ['radio', 'checkbox', 'checkboxes', 'dropdown', 'multiselect'].includes(question.answerStyle);
      const isCommentOnlyQuestion = question.showComments && 
                                    !question.showFiveStar && 
                                    (!needsAnswerOptions || !hasAnswerOptions);
      
      if (!hasValue && isCommentOnlyQuestion && response?.comment && response.comment.trim() !== '') {
        hasValue = true;
      }

      if (!hasValue) {
        error = `${question.name} is required`;
      }
    }

    // Type-specific validation
    if (response?.value && !error) {
      switch (question.answerStyle) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(response.value)) {
            error = 'Please enter a valid email address';
          }
          break;
        case 'url':
          try {
            new URL(response.value);
          } catch {
            error = 'Please enter a valid URL';
          }
          break;
        case 'number':
          if (isNaN(Number(response.value))) {
            error = 'Please enter a valid number';
          }
          break;
      }

      // Character limit validation
      if (question.options?.useCharacterLimit && question.options?.characterLimit && !error) {
        if (response.value.length > question.options.characterLimit) {
          error = `Maximum ${question.options.characterLimit} characters allowed`;
        }
      }
    }

    return error;
  }, [surveyState.surveyData, surveyState.touchedFields]);

  return {
    validatePage,
    validateField,
  };
};